import { useCallback, useEffect, useRef, useState } from 'react';
import useCounterSync from './useCounterSync';

const RETRY_DELAYS_MS = [3_000, 10_000, 30_000];

interface UseLiveCounterOptions<T> {
  /** Resolves the current value. The abort controller is cancelled when a newer fetch starts. */
  fetcher: (abortController: AbortController) => Promise<T>;
  enabled?: boolean;
  intervalMs?: number;
}

interface UseLiveCounterResult<T> {
  value: T | null;
  refresh: () => void;
}

/**
 * Owns the value of a badge counter and guarantees it converges to the server state.
 *
 * On top of the resync triggers provided by `useCounterSync` it retries failed requests
 * with a backoff - a counter that silently fails on the first fetch used to stay hidden
 * until a full page reload - and discards out-of-order responses.
 */
export default function useLiveCounter<T>({
  fetcher,
  enabled = true,
  intervalMs,
}: UseLiveCounterOptions<T>): UseLiveCounterResult<T> {
  const [value, setValue] = useState<T | null>(null);

  const fetcherRef = useRef(fetcher);
  const abortRef = useRef<AbortController>();
  const retryTimerRef = useRef<number>();
  const retryAttemptRef = useRef(0);
  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  fetcherRef.current = fetcher;

  const run = useCallback(async () => {
    window.clearTimeout(retryTimerRef.current);
    abortRef.current?.abort();

    const abortController = new AbortController();
    abortRef.current = abortController;

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    const isStale = () => !isMountedRef.current || requestId !== requestIdRef.current;

    try {
      const result = await fetcherRef.current(abortController);

      if (isStale()) {
        return;
      }

      retryAttemptRef.current = 0;
      setValue(result);
    } catch (error) {
      if (isStale() || abortController.signal.aborted) {
        return;
      }

      const delay = RETRY_DELAYS_MS[Math.min(retryAttemptRef.current, RETRY_DELAYS_MS.length - 1)];
      retryAttemptRef.current += 1;
      retryTimerRef.current = window.setTimeout(run, delay);
    }
  }, []);

  const refresh = useCounterSync(run, { enabled, intervalMs });

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
      window.clearTimeout(retryTimerRef.current);
    };
  }, []);

  return { value, refresh };
}
